import { debug } from "./debugLogger";
// src/utils/themeUtils.ts
/**
 * Theme utility functions for color manipulation and CSS variable management
 * Following separation of concerns - these are pure utility functions
 */

export interface ThemeColors {
  primary: string;
  /** Filled-button hover (lighter than primary in light UI) */
  primaryHover: string;
  primaryLight: string;
  primaryDark: string;
  secondary: string;
  background: string;
}

/** Defaults match `index.css` brand tokens (#2f4941 / #4d7368 hover, etc.) */
export const DEFAULT_THEME_COLORS: ThemeColors = {
  primary: "#2f4941",
  primaryHover: "#4d7368",
  primaryLight: "#648b7f",
  primaryDark: "#1e3128",
  secondary: "#83B1AB",
  background: "#F7F5F0",
};

const INLINE_BRAND_VAR_KEYS = [
  "--color-brand-primary",
  "--color-brand-primary-hover",
  "--color-brand-primary-light",
  "--color-brand-primary-dark",
  "--color-brand-secondary",
  "--color-brand-background",
] as const;

function normalizeHex(s: string): string {
  return s.trim().toLowerCase();
}

function themeColorsEqual(a: ThemeColors, b: ThemeColors): boolean {
  return (
    normalizeHex(a.primary) === normalizeHex(b.primary) &&
    normalizeHex(a.primaryHover) === normalizeHex(b.primaryHover) &&
    normalizeHex(a.primaryLight) === normalizeHex(b.primaryLight) &&
    normalizeHex(a.primaryDark) === normalizeHex(b.primaryDark) &&
    normalizeHex(a.secondary) === normalizeHex(b.secondary) &&
    normalizeHex(a.background) === normalizeHex(b.background)
  );
}

/**
 * Remove inline `--color-brand-*` from `<html>` so `index.css` can drive light/dark
 * (`:root` vs `html.dark` / system). Inline author styles override stylesheet
 * custom properties on the same element, which was forcing light-only brand colors in dark mode.
 */
export function clearInlineBrandThemeVariables(): void {
  const root = document.documentElement;
  for (const key of INLINE_BRAND_VAR_KEYS) {
    root.style.removeProperty(key);
  }
}

/**
 * Lighten a color by a specified percentage
 * @param color - Hex color string (e.g., "#2f4941")
 * @param percent - Percentage to lighten (0-100)
 * @returns Lightened hex color string
 */
export function lightenColor(color: string, percent: number): string {
  try {
    const num = parseInt(color.replace("#", ""), 16);
    const r = Math.min(255, (num >> 16) + Math.round(255 * (percent / 100)));
    const g = Math.min(255, ((num >> 8) & 0x00FF) + Math.round(255 * (percent / 100)));
    const b = Math.min(255, (num & 0x0000FF) + Math.round(255 * (percent / 100)));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  } catch (error) {
    debug.error("Error lightening color:", error);
    return color;
  }
}

/**
 * Darken a color by a specified percentage
 * @param color - Hex color string (e.g., "#2f4941")
 * @param percent - Percentage to darken (0-100)
 * @returns Darkened hex color string
 */
export function darkenColor(color: string, percent: number): string {
  try {
    const num = parseInt(color.replace("#", ""), 16);
    const r = Math.max(0, (num >> 16) - Math.round(255 * (percent / 100)));
    const g = Math.max(0, ((num >> 8) & 0x00FF) - Math.round(255 * (percent / 100)));
    const b = Math.max(0, (num & 0x0000FF) - Math.round(255 * (percent / 100)));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  } catch (error) {
    debug.error("Error darkening color:", error);
    return color;
  }
}

/**
 * Apply brand colors as inline CSS variables on `<html>` (API / custom brand only).
 * Catalog defaults are **not** applied inline — `index.css` owns light + dark tokens.
 */
export function updateCSSVariables(colors: ThemeColors): void {
  if (themeColorsEqual(colors, DEFAULT_THEME_COLORS)) {
    clearInlineBrandThemeVariables();
    return;
  }

  const root = document.documentElement;
  root.style.setProperty("--color-brand-primary", colors.primary);
  root.style.setProperty(
    "--color-brand-primary-hover",
    colors.primaryHover ||
      lightenColor(colors.primary, 12) ||
      DEFAULT_THEME_COLORS.primaryHover
  );
  root.style.setProperty("--color-brand-primary-light", colors.primaryLight);
  root.style.setProperty("--color-brand-primary-dark", colors.primaryDark);
  root.style.setProperty("--color-brand-secondary", colors.secondary);
  root.style.setProperty("--color-brand-background", colors.background);
}

/**
 * Extract theme colors from brand data
 * Uses primary and secondary colors from brand, calculates variants
 * Falls back to defaults if colors are missing
 * @param brandColors - Array of color objects from Brand type
 * @returns ThemeColors object
 */
export function extractThemeColors(
  brandColors?: Array<{ value: string; type: "primary" | "secondary" }>
): ThemeColors {
  const primaryColor = brandColors?.find((c) => c.type === "primary")?.value || DEFAULT_THEME_COLORS.primary;
  const secondaryColor = brandColors?.find((c) => c.type === "secondary")?.value || DEFAULT_THEME_COLORS.secondary;

  const hover =
    lightenColor(primaryColor, 14) || DEFAULT_THEME_COLORS.primaryHover;
  return {
    primary: primaryColor,
    primaryHover: hover,
    primaryLight: lightenColor(primaryColor, 28) || DEFAULT_THEME_COLORS.primaryLight,
    primaryDark: darkenColor(primaryColor, 18) || DEFAULT_THEME_COLORS.primaryDark,
    secondary: secondaryColor,
    background: DEFAULT_THEME_COLORS.background, // Keep default background
  };
}